import { actions as webdriverCommandActions } from '@saucelabs/sl-module-webdriver-commands';
import { actions as espressoCommandActions } from '@saucelabs/sl-module-espresso-commands';
import { actions as buildsActions } from '@saucelabs/sl-module-builds';
import { actions as jobActions, operations as jobOperations } from '@saucelabs/sl-module-jobs';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { constants as notificationConstants } from '@saucelabs/sl-module-notifications';
import { utils as authUtils } from '@saucelabs/sl-container-authentication';
import { actions as modalActions } from '@saucelabs/sl-container-modal';
import { createApiAction } from '@saucelabs/sl-util-redux';
import { parse } from 'qs';
import last from 'lodash.last';
import throttle from 'lodash.throttle';
import { actions as performanceActions } from '@saucelabs/sl-module-performance';
import { actions as errorPageActions } from '@saucelabs/sl-container-error-pages';
import { findActiveCommand } from '@saucelabs/sl-module-webdriver-commands/lib/functions';

import { actions as fileActions, constants as fileConstants, utils as fileUtils } from './../file';
import { actions as containerJobActions, operations as containerJobOperations } from './../job';
import * as actions from './actions';
import * as constants from './constants';
import * as types from './types';
import { newActiveCommand, hasUserAccessToTheJob, updateVideoTimeline } from './utils';
import {
  commandSelector,
  filteredCommandsSelector,
  playerSelector,
  videoPlayerJobDataSelector,
  networkRequestParamSelector,
} from './selectors';
import { CTA_EVENTS, JOB_DETAILS_LEGACY_EVENTS, JOB_DETAILS_EVENTS, trackEvent } from './../../tracking';

import { operations } from './index';

export const getJob = (payload, meta) => (dispatch) => (
  dispatch(jobOperations.getJob(payload, meta))
    .then((data) => {
      /**
       * don't show errors for testrunner (DevX) jobs as they currently use
       * invalid capabilities to create job entries in order to not have to
       * spin up VMs. This will be cleaned up soon when testrunner jobs are
       * stored in their own data storage service.
       * https://saucedev.atlassian.net/browse/DEVX-52
       */
      if (data.base_config && data.base_config['sauce:options']?.devX) {
        return data;
      }

      if (data.error) {
        const notification = dispatch(notificationActions.notifyWarning(
          data.error,
          'There is a problem with your test.',
          true,
        ));
        dispatch(actions.appendNotification(notification.payload.id));
      }
      return data;
    })
);

export const getAsset = (payload) => (dispatch) => (
  dispatch(jobActions.getAsset(payload, { skipErrorPage: true }))
);

export const getRdcAsset = (payload) => (dispatch) => (
  dispatch(jobActions.getRdcAsset(payload, { skipErrorPage: true }))
    .catch(() => {
      const notification = dispatch(notificationActions.notifyDanger(
        payload.url, 'Failed to fetch test report asset.', true,
      ));
      dispatch(actions.appendNotification(notification.payload.id));
    })
);

export const fetchJob = (payload) => (dispatch) => (
  dispatch(operations.getJob(payload, { skipErrorPage: true }))
    .catch((slError) => (
      dispatch(jobActions.getUPJob(payload))
        .catch(() => {
          throw slError;
        })
    ))
);

export const initialize = createApiAction(
  types.INITIALIZE,
  (payload, meta, dispatch, getState) => {
    const initialState = getState();
    const {
      user,
      router: { location: { pathname: currentPathname, search } },
    } = initialState;

    const qs = parse(search, { ignoreQueryPrefix: true });
    const authInQS = !!qs.auth;

    return dispatch(operations.fetchJob(payload))
      .then((response) => {
        const job = getState().apps.job.job.get('job');
        if ((!user?.isAuthenticated() && !authInQS) && !job.isPublic()) {
          throw authUtils.redirectToMainLogin(currentPathname);
        }

        if (user && !hasUserAccessToTheJob({ user, job })) {
          return dispatch(errorPageActions.showNotFoundPage());
        }

        dispatch(actions.recordTrackViewed(
          response.base_config,
          { id: response.id, error: response.error },
        ));
        return response;
      }).catch((errResponse) => {
        dispatch(actions.recordTrackViewed(
          errResponse.base_config,
          { id: errResponse.id, error: errResponse.error },
        ));
        if (!user?.isAuthenticated()) {
          throw authUtils.redirectToMainLogin(currentPathname);
        }
        throw errResponse;
      });
  },
);

// If job has commands, returns the filename of commands log
// Otherwise, returns the filename of first found log
export const getPrimaryLogFilename = (job, assets) => {
  if (job.hasCommands()) {
    if (job.isEspresso()) {
      return fileConstants.ESPRESSO_LOGFILE_NAME;
    }
    return fileConstants.SELENIUM_LOGFILE_NAME;
  }

  const logFileEntries = fileUtils.logFilesOnly(assets);
  if (!logFileEntries.length) {
    return null;
  }
  const [[, firstLogFilename]] = logFileEntries;
  return firstLogFilename;
};

export const parseLogfile = (job, logData) => (dispatch) => {
  if (job.isEspresso()) {
    return dispatch(espressoCommandActions.parseEspressoLog(logData));
  }
  return dispatch(webdriverCommandActions.parseSeleniumLog(logData));
};

export const tryGetBuildByName = (job) => (dispatch) => {
  if (!job.getBuildName()) {
    return Promise.resolve();
  }

  return dispatch(buildsActions.getBuildByName({
    buildName: job.getBuildName(),
    username: job.get('owner'),
  }, { skipErrorPage: true }));
};

export const fetchConsoleLogAsset = ({ job }) => (dispatch) => {
  if (job.isExtendedDebugging() && job.hasExtendedDebuggingSupport()) {
    dispatch(jobActions.setAssetList({ 'console.json': 'console.json' }));
  }
};

let tryGetSLAssetsTimeout;
export const tryGetSLAssets = ({ auth, job, config }) => (dispatch) => {
  if (!job.showAssets()) {
    clearTimeout(tryGetSLAssetsTimeout);
    dispatch(containerJobActions.assetsExpired());
    const notification = dispatch(notificationActions.notifyDanger(
      constants.MISSING_ASSETS_MESSAGE, 'Test assets have expired.', true,
    ));
    dispatch(trackEvent(JOB_DETAILS_EVENTS.EXPIRED_ASSETS));
    dispatch(actions.appendNotification(notification.payload.id));
    return Promise.resolve();
  }

  const { id } = job;
  return dispatch(jobActions.getAssetsList({ id, config }, { skipErrorPage: true }))
    .then(({ data }) => {
      clearTimeout(tryGetSLAssetsTimeout);
      const logfileName = operations.getPrimaryLogFilename(job, data);
      dispatch(operations.fetchConsoleLogAsset({ job }));
      return dispatch(operations.getAsset(
        { id, auth, assetName: logfileName, config },
        { skipErrorPage: true },
      ))
        .then((logFile) => {
          dispatch(fileActions.attachFile({ name: logfileName, data: logFile.data }));
          dispatch(fileActions.selectFile(logfileName));
          if (job.hasCommands()) {
            dispatch(operations.parseLogfile(job, logFile.data));
          }
        });
    })
    .catch(({ response }) => {
      if (response && response.data && response.data === constants.ASSETS_NOT_AVAILABLE_YET) {
        tryGetSLAssetsTimeout = setTimeout(() => {
          dispatch(tryGetSLAssets({ job, config }));
        }, constants.API_FETCH_INTERVAL);
      }
    });
};

export const fetchSLJobAdditionalData = ({ auth, job, config }) => (dispatch) => {
  dispatch(operations.tryGetBuildByName(job));
  return dispatch(operations.tryGetSLAssets({ auth, job, config }));
};

export const fetchRdcJobAdditionalData = ({ job }) => (dispatch) => {
  const assetsInfo = {};

  if (job.video_url) {
    assetsInfo.video = job.video_url;
  }

  if (job.screenshots.length) {
    assetsInfo.screenshots = job.screenshots;
  }

  const deviceLogRequest = dispatch(getRdcAsset({ url: job.get('device_log_url') })).then(
    (deviceLog) => {
      if (deviceLog?.data) {
        assetsInfo['device.log'] = 'device.log';
        dispatch(fileActions.attachFile({
          name: 'device.log',
          data: fileActions.formatRdcLogFile(deviceLog.data),
        }));
        dispatch(jobActions.setAssetList(assetsInfo));
      }
    },
  );

  const frameworkLogRequest = dispatch(getRdcAsset({ url: job.get('framework_log_url') })).then(
    (frameworkLog) => {
      if (frameworkLog?.data) {
        assetsInfo['appium.log'] = 'appium.log';
        dispatch(fileActions.attachFile({
          name: 'appium.log',
          data: fileActions.formatRdcLogFile(frameworkLog.data),
        }));
        dispatch(jobActions.setAssetList(assetsInfo));
      }
    },
  );


  const commandsRequest = dispatch(getRdcAsset({ url: job.get('requests_url') })).then(
    (deviceRequests) => {
      if (deviceRequests?.data) {
        assetsInfo['commands.json'] = 'commands.json';
        dispatch(fileActions.attachFile({
          name: 'commands.json',
          data: deviceRequests.data,
        }));
        dispatch(webdriverCommandActions.parseAppiumLog(deviceRequests.data));
        dispatch(jobActions.setAssetList(assetsInfo));
      }
    },
  );

  Promise.race([deviceLogRequest, frameworkLogRequest, commandsRequest]).then(() => {
    dispatch(fileActions.selectFile(last(Object.values(assetsInfo))));
  });
};

export const fetchAdditionalData = ({ auth, job, config }) => (dispatch) => {
  dispatch(trackEvent(JOB_DETAILS_LEGACY_EVENTS.PAGE_OPEN));
  if (job.isRdc()) {
    return dispatch(fetchRdcJobAdditionalData({ job }));
  }

  return dispatch(fetchSLJobAdditionalData({ auth, job, config }));
};

const throttledNotifyLinkCopied = throttle(
  (dispatch) => dispatch(notificationActions.notify({
    category: notificationConstants.CATEGORY_SUCCESS,
    text: 'URL address is copied to your clipboard.',
  })),
  constants.NOTIFICATION_THROTTLE_INTERVAL,
  { trailing: false },
);

const throttledNotifyLinkFailedToCopy = throttle(
  (dispatch) => dispatch(notificationActions.notify({
    category: notificationConstants.CATEGORY_SUCCESS,
    text: 'URL address is copied to your clipboard.',
  })),
  constants.NOTIFICATION_THROTTLE_INTERVAL,
  { trailing: false },
);

export const notifyLinkCopied = () => (dispatch) => throttledNotifyLinkCopied(dispatch);

export const notifyLinkFailedToCopy = () => (dispatch) => throttledNotifyLinkFailedToCopy(dispatch);

const throttledUpdateActiveCommand = throttle(
  (dispatch, payload) => dispatch(containerJobOperations.updateActiveCommand(payload)),
  500,
  { trailing: false },
);

export const onVideoPlayerMounted = ({ player }) => (dispatch, getState) => {
  const jobData = videoPlayerJobDataSelector(getState());
  dispatch(actions.videoPlayerMounted(player));

  player.on('play', () => {
    trackEvent(CTA_EVENTS.PLAY_VIDEO);
  });
  player.on('timeupdate', () => (
    throttledUpdateActiveCommand(
      dispatch,
      {
        currentTime: player.currentTime(),
        timestamp: jobData.startTime ? jobData.startTime + (player.currentTime() * 1000) : null,
      },
    )
  ));
};

export const videoJumpToCommand = (command) => (dispatch, getState) => {
  const player = playerSelector(getState());
  // if we return `timelineSeconds`, then it will start on the last milisecond of
  // previous command. Adding small offset to be sure we will select a proper command.
  const currentTime = command.getTimeline() + 0.0001;
  updateVideoTimeline({ player, currentTime });
};

export const videoJumpToCommandId = (commandId) => (dispatch, getState) => (
  dispatch(operations.videoJumpToCommand(commandSelector(commandId)(getState())))
);

export const selectNetworkRequest = (payload) => (dispatch, getState) => {
  const { commands, currentTime, player } = networkRequestParamSelector(getState(), payload);
  const command = findActiveCommand({ commands, currentTime });
  dispatch(containerJobOperations.updateActiveCommand({
    commandId: command.get('id'),
  }));
  updateVideoTimeline({ player, currentTime });
};

export const videoPlayPause = () => (dispatch, getState) => {
  const player = playerSelector(getState());

  if (player.paused()) {
    player.play();
  } else {
    player.pause();
  }
};

const navigateCommandByOffset = (offset) => (dispatch, getState) => {
  const commands = filteredCommandsSelector(getState());
  const currentActive = commands.findIndex((command) => command.active);
  const newActiveId = newActiveCommand(commands, offset, currentActive);
  dispatch(operations.videoJumpToCommandId(newActiveId));
  // Updating the video triggers active command update anyway, but it's too slow:
  dispatch(containerJobOperations.updateActiveCommand({
    collapseCurrent: true,
    commandId: newActiveId,
  }));
};

export const navigateCommandPrevious = () => navigateCommandByOffset(-1);
export const navigateCommandNext = () => navigateCommandByOffset(1);

export const setFilterPhrase = (phrase) => actions.updateCommandsFilter({ phrase });
export const setHasScreenshot = (
  (hasScreenshot) => actions.updateCommandsFilter({ hasScreenshot })
);

export const setHasRootCause = (
  (hasRootCause) => actions.updateCommandsFilter({ hasRootCause })
);

export const cleanup = ({ notifications }) => (dispatch) => (
  Promise.all([
    dispatch(actions.cleanup()),
    dispatch(fileActions.cleanup()),
    dispatch(containerJobActions.cleanup()),
    dispatch(performanceActions.cleanup()),
    ...notifications.map((notificationId) => (
      dispatch(notificationActions.hideNotification(notificationId))
    )),
  ])
);

export const createLiveSessionPreviewNotification = (isFreeUser) => (dispatch) => {
  let description = 'Some features will be disabled until the test has finished running.';
  if (!isFreeUser) {
    description += ' You can click on video to interact and take over the running test';
  }

  const notification = dispatch(notificationActions.notifyInfo(
    description,
    'This test is currently running...',
    true,
  ));
  dispatch(actions.appendNotification(notification.payload.id));
  return () => (
    dispatch(notificationActions.hideNotification(notification.payload.id))
  );
};

export const reportJobIssue = (payload) => (dispatch) => (
  dispatch(actions.reportJobIssue(payload))
    .then(() => {
      dispatch(notificationActions.notifySuccess('Issue successfully reported!'));
      dispatch(modalActions.hideModal(constants.REPORT_JOB_ISSUE_MODAL));
    })
);
